"use client";

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import {
  type ActivityPostLikeListResponse,
  listActivityPostLikes,
} from "@/actions/activity-post";

type LikesPage = ActivityPostLikeListResponse;

export const activityLikesKeys = {
  likes: (activityPostId: number) =>
    ["activityPosts", "likes", activityPostId] as const,
};

export function useActivityPostLikes(
  activityPostId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 30 } = options ?? {};

  return useInfiniteQuery({
    queryKey: activityLikesKeys.likes(activityPostId ?? 0),
    enabled: Boolean(activityPostId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!activityPostId) {
        return {
          likes: [],
          hasMore: false,
          nextCursor: null,
        } satisfies LikesPage;
      }

      const result = await listActivityPostLikes({
        activityPostId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch likes");
      }

      return result.data;
    },
    initialPageParam: null as LikesPage["nextCursor"],
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });
}
